/**
 * Map Slot Registry
 *
 * Governed live-interactive-map registry. A page/report NEVER injects raw
 * Leaflet/map JSX into a DS section. Instead it references a live-map SLOT KEY,
 * and a DS/feature-registration module registers the actual renderer ONCE here
 * (exactly as media assets are referenced by key via mediaRegistry.ts).
 *
 * This is the sealed mechanism behind story media descriptors:
 *   figure = { kind: 'live', slotKey: 'risk-workspace.stands', slotProps: {...} }
 * The DS resolves the registered renderer and hands it only sealed, semantic
 * slot props (selected feature id, feature-select callback, layer ids,
 * interaction mode). No dimensions, no styling, no direct tile URLs, no JSX
 * cross the media element boundary (elements/FmMediaElements.tsx).
 *
 * Renderers are presentation-only and MUST NOT call fetch/API clients, own
 * React Query, import route hooks, or derive server state. Domain hooks and
 * pure builders publish resolved data for the renderer to consume.
 *
 * muem9.19 (risk workspace) and story sections register real map renderers
 * against this registry; page/report descriptors stay library-agnostic.
 */

import React from "react";
import governedMapSlotRenderers from "./governedMapSlotRenderers.json";
import { lazySlotRenderer } from "./lazySlotBoundary";
import type {
  FmMapSlotInteractionMode,
  FmMapSelectedFeatureKind,
  FmMeasurementPlotRef,
  FmPrimaryMapWorkspaceData,
  FmRankedSpatialMapData,
  FmDatedRasterOverlay,
} from "@/shared/config/mapSlotContracts";
import type { FmMapPresentation } from "@/shared/config/mapPresentation";

export type { FmMapPresentation } from "@/shared/config/mapPresentation";
export type {
  FmMapSlotInteractionMode,
  FmMapSelectedFeatureKind,
  FmMeasurementPlotMappingStatus,
  FmMeasurementPlotRef,
  FmPrimaryMapWorkspaceData,
  FmRankedSpatialMapData,
  FmRankedSpatialRecord,
  FmDatedRasterOverlay,
} from "@/shared/config/mapSlotContracts";

/**
 * Semantic interaction mode a governed live map may run in.
 * The DS/registered renderer owns what each mode means visually.
 */
/**
 * Sealed props a governed live-map slot renderer receives. Exact typed data,
 * domain callbacks, and semantic hints only — never styling, tile URLs, or JSX.
 */
export interface FmMapSlotProps {
  /** Property id for property-scoped governed map slots. */
  propertyId?: string;
  /** Optional caption for static governed map snapshots. */
  caption?: string;
  /** Semantic snapshot size for static governed map slots. */
  snapshotSize?: "preview" | "compact" | "standard" | "tall";
  /** Currently selected feature/stand id (drives highlight + callout sync). */
  selectedFeatureId?: string;
  /** Independent controlled identities for map-review workflows that compare plots and stands. */
  selectedPlotId?: string;
  selectedStandId?: string;
  highlightedPlotIds?: readonly string[];
  /** Feature identities still visible after the workspace's governed filters. */
  visibleFeatureIds?: readonly string[];
  /** Typed selection identity; prevents renderers from guessing the domain kind. */
  selectedFeatureKind?: FmMapSelectedFeatureKind;
  /** Domain callback when a feature is chosen inside the live map. */
  onFeatureSelect?: (featureId: string) => void;
  /**
   * Multi-select mode. Set only by the map element's governed selection
   * (`FmMapElement.selection`), never by a descriptor: the chosen set, the
   * features the owner may choose, and the armed lasso. A click still reports
   * through `onFeatureSelect`; the element owns toggling and set arithmetic.
   */
  selectedFeatureIds?: readonly string[];
  selectableFeatureIds?: readonly string[];
  lasso?: FmMapSlotLasso;
  /** Distinct callbacks for maps that expose plots and stands in one assignment flow. */
  onPlotSelect?: (plotId: string) => void;
  onStandSelect?: (standId: string, propertyId?: string) => void;
  /** Semantic layer ids the slot should show (registry/renderer owns styling). */
  layerIds?: readonly string[];
  /** Stable plot identities used by deterministic previews and selection handoff. */
  measurementPlotRefs?: readonly FmMeasurementPlotRef[];
  /** Selected governed basemap identity; the renderer owns its tile source. */
  baseMapId?: "streets" | "satellite" | "terrain";
  /** Semantic interaction mode. */
  interactionMode?: FmMapSlotInteractionMode;
  /** Five-mode workspace intent; the renderer translates it into map behaviour. */
  workspaceMode?: "find" | "orient" | "inspect" | "risk" | "refine";
  /** Domain source for stand-level inventory map slots. */
  measurementSource?: string | null;
  /** Active measurement campaign for stand-level inventory map slots. */
  activeMeasurementCampaignId?: number | string | null;
  /** Baseline date used by stand-level inventory map timeline controls. */
  baselineMetsakeskusDate?: string | null;
  /** Stable viewport identity; registered renderers own fit/pan/zoom mechanics. */
  viewportRef?: {
    id: string;
    revision: string;
    frame: "all" | "property" | "selection" | "risk";
  };
  /** Stable geometry identities only; raw GeoJSON never crosses the slot boundary. */
  geometryRefs?: readonly string[];
  /** Immutable source id plus semantic filter values for governed workspace maps. */
  sourceId?: string;
  workspaceFilters?: Readonly<Record<string, readonly string[]>>;
  /** Gallery-only deterministic renderer path; never set by production routes. */
  preview?: boolean;
  /**
   * The one validated bag of presentation hints (basemap lens and tone,
   * stage, fit target, reveal, stand fill, feature categories, tooltips).
   * Each slot renders only the hints it registers; any other is refused.
   */
  presentation?: FmMapPresentation;
  /**
   * Semantic dated-raster overlay status for Analysis preview and production
   * chrome. Signed image URLs stay on `mapData.datedRasterOverlay`.
   */
  datedRaster?:
    | Extract<FmDatedRasterOverlay, { status: "unavailable" }>
    | { status: "ready"; compared?: boolean };
}

/** The lasso the map element arms; the renderer draws it and reports what it enclosed. */
export interface FmMapSlotLasso {
  active: boolean;
  onActiveChange: (active: boolean) => void;
  onComplete: (featureIds: readonly string[]) => void;
}

export type FmPrimaryMapSlotMode =
  | { preview: true; mapData: null }
  | { preview?: false; mapData: FmPrimaryMapWorkspaceData };

export type FmPrimaryMapSlotProps =
  Omit<FmMapSlotProps, "preview"> & FmPrimaryMapSlotMode;
export interface FmRankedSpatialMapSlotProps extends FmMapSlotProps {
  mapData: FmRankedSpatialMapData | null;
}
export type FmMapSlotDescriptorProps =
  | FmMapSlotProps
  | FmPrimaryMapSlotProps
  | FmRankedSpatialMapSlotProps;

/** Presentation-only registered renderer. Returns a DS-owned map element and never fetches. */
export type FmMapSlotRenderer = (props: FmMapSlotProps) => React.ReactElement;
export type FmMapSlotRendererFor<Props extends FmMapSlotProps> = (
  props: Props,
) => React.ReactElement;
export type FmMapSlotModuleFor<Props extends FmMapSlotProps> = {
  default: React.ComponentType<Props>;
};

const MAP_SLOT_REGISTRY = new Map<string, FmMapSlotRenderer>();

export const PROPERTY_BOUNDARY_SNAPSHOT_SLOT_KEY =
  "property.boundary-snapshot" as const;
export const BROWSE_PARCEL_PUBLIC_PICTURE_SLOT_KEY =
  "browse.parcel-public-picture" as const;
export const STAND_INSPECTION_MAP_SLOT_KEY = "stand.inspection-map" as const;
export const REPORT_RANKED_SPATIAL_MAP_SLOT_KEY =
  "report.ranked-spatial-evidence" as const;
export const WORKSPACE_PRIMARY_MAP_SLOT_KEY = "workspace.map.primary" as const;
export const MEASUREMENTS_CAMPAIGN_MAPPING_SLOT_KEY =
  "measurements.campaign-mapping" as const;
export const MEASUREMENTS_UPLOAD_PROPERTY_SLOT_KEY =
  "measurements.upload-property-check" as const;
export const MEASUREMENTS_STAND_SELECTION_SLOT_KEY =
  "measurements.property-stand-selection" as const;
/**
 * Gallery preview identity for the operational workspace's decision map.
 *
 * It lives here, beside the governed production identities, rather than in the
 * specimen file, because a specimen that mints its own slot key is how the
 * gallery ended up rendering a grey gradient with three text pills in the media
 * frame instead of a map. Its renderer is
 * `components/ui/FmOperationalDecisionMapPreview.tsx`, held to the same
 * presentation-only rules as every other `Fm*` module. It is a PREVIEW identity:
 * it is deliberately absent from `governedMapSlotRenderers.json`, which records
 * only production slots, and a production route must never reference it.
 */
export const OPERATIONAL_DECISION_MAP_PREVIEW_SLOT_KEY =
  "preview.operational.decision-map" as const;
export const OPS_YEAR_STANDS_SLOT_KEY =
  "operations-planning.year-stands" as const;
/** Property stands chosen as a set: click, lasso and the element's list. */
export const STAND_MULTI_SELECT_MAP_SLOT_KEY = "stands.multi-select" as const;
/** Slots whose renderer honours the multi-select props; `FmMapElement.selection` names one. */
export const MULTI_SELECT_MAP_SLOT_KEYS: ReadonlySet<string> = new Set([
  STAND_MULTI_SELECT_MAP_SLOT_KEY,
]);
export const MEASUREMENT_MAP_SLOT_KEYS = [
  MEASUREMENTS_CAMPAIGN_MAPPING_SLOT_KEY,
  MEASUREMENTS_UPLOAD_PROPERTY_SLOT_KEY,
  MEASUREMENTS_STAND_SELECTION_SLOT_KEY,
] as const;
export type WorkspacePrimaryMapSlotKey = typeof WORKSPACE_PRIMARY_MAP_SLOT_KEY;
export type MeasurementMapSlotKey = (typeof MEASUREMENT_MAP_SLOT_KEYS)[number];
export type PrimaryMapSlotKey = WorkspacePrimaryMapSlotKey | MeasurementMapSlotKey;
export type GovernedMapSlotKey =
  | typeof PROPERTY_BOUNDARY_SNAPSHOT_SLOT_KEY
  | typeof BROWSE_PARCEL_PUBLIC_PICTURE_SLOT_KEY
  | typeof STAND_INSPECTION_MAP_SLOT_KEY
  | typeof REPORT_RANKED_SPATIAL_MAP_SLOT_KEY
  | typeof WORKSPACE_PRIMARY_MAP_SLOT_KEY
  | typeof OPS_YEAR_STANDS_SLOT_KEY
  | typeof STAND_MULTI_SELECT_MAP_SLOT_KEY
  | MeasurementMapSlotKey;

type MapSlotPropsFor<Key extends GovernedMapSlotKey> =
  Key extends WorkspacePrimaryMapSlotKey | MeasurementMapSlotKey
    ? FmPrimaryMapSlotProps
    : Key extends typeof REPORT_RANKED_SPATIAL_MAP_SLOT_KEY
      ? FmRankedSpatialMapSlotProps
      : FmMapSlotProps;

/** Canonical source owners for architecture guards and tooling. */
export const GOVERNED_MAP_SLOT_RENDERER_MODULES: Readonly<
  Record<GovernedMapSlotKey, string>
> = Object.freeze(governedMapSlotRenderers);

declare const previewMapSlotKeyBrand: unique symbol;
export type PreviewMapSlotKey = string & {
  readonly [previewMapSlotKeyBrand]: true;
};

/** Production slot identities stay governed even before their renderers are lazy-loaded. */
const GOVERNED_MAP_SLOT_KEYS: ReadonlySet<string> = new Set<GovernedMapSlotKey>(
  [
    PROPERTY_BOUNDARY_SNAPSHOT_SLOT_KEY,
    BROWSE_PARCEL_PUBLIC_PICTURE_SLOT_KEY,
    STAND_INSPECTION_MAP_SLOT_KEY,
    REPORT_RANKED_SPATIAL_MAP_SLOT_KEY,
    WORKSPACE_PRIMARY_MAP_SLOT_KEY,
    OPS_YEAR_STANDS_SLOT_KEY,
    STAND_MULTI_SELECT_MAP_SLOT_KEY,
    ...MEASUREMENT_MAP_SLOT_KEYS,
  ],
);
const TEST_MAP_SLOT_KEYS = new Set<string>();
const PREVIEW_MAP_SLOT_KEYS = new Set<string>();
const PREVIEW_MAP_SLOT_RENDERERS = new Map<string, FmMapSlotRenderer>();
const PREVIEW_MAP_SLOT_DISPATCHERS = new Map<string, FmMapSlotRenderer>();

export function isPrimaryMapSlotKey(key: string): key is PrimaryMapSlotKey {
  return key === WORKSPACE_PRIMARY_MAP_SLOT_KEY
    || MEASUREMENT_MAP_SLOT_KEYS.some((slotKey) => slotKey === key);
}

function invalidateMapSlotDispatcher(key: string): void {
  PREVIEW_MAP_SLOT_DISPATCHERS.delete(key);
}

/**
 * Register a live-map slot renderer under a stable key. Idempotent-by-overwrite;
 * call once from a DS/feature registration module (e.g. the risk workspace).
 */
export function registerMapSlot<Key extends GovernedMapSlotKey>(
  key: Key,
  renderer: FmMapSlotRendererFor<MapSlotPropsFor<Key>>,
): void {
  MAP_SLOT_REGISTRY.set(key, renderer as FmMapSlotRenderer);
  invalidateMapSlotDispatcher(key);
}

/**
 * Register a code-split renderer from the application composition root.
 *
 * The registration OWNS the suspension it creates. A bare
 * `React.lazy` renderer suspends to whatever boundary happens to be above it,
 * and for every consumer except FmMapWorkspaceMap that is the route-level
 * `<Suspense fallback={<RouteSpinner/>}>` in App.tsx, which covers the entire
 * `<Routes>` tree. React hides a suspended boundary's host children with
 * `display:none`, so one map chunk arriving late blanked the whole application:
 * an owner with the plan selector open lost DOM focus (a hidden ancestor blurs
 * the focused element), the AntD list closed under them, and nothing reopened
 * it. Reproduced at the public boundary in runs 34165521513, 34166330722,
 * 34166491492, 34166500685 and 34167480863 - the same input node, the same
 * popup, aria-expanded=false, and focus on <body>.
 *
 * The boundary must EXIST. Its fallback was `null`, and a nearer boundary
 * PREEMPTS the consumer's own, so FmMapWorkspaceMap's fallback could never run
 * and the primary and measurement maps loaded behind a blank canvas with no
 * loading affordance at all. `lazySlotRenderer` serves an observable one by
 * default and takes an override, so the frame the region owns is never left
 * carrying nothing.
 */
export function registerLazyMapSlot<Key extends GovernedMapSlotKey>(
  key: Key,
  loader: () => Promise<FmMapSlotModuleFor<MapSlotPropsFor<Key>>>,
  fallback?: React.ReactNode,
): void {
  MAP_SLOT_REGISTRY.set(key, lazySlotRenderer(key, loader, fallback) as FmMapSlotRenderer);
  invalidateMapSlotDispatcher(key);
}

/** Test-only registration lane; reset removes both identity and renderer. */
export function __registerMapSlotForTest(
  key: string,
  renderer: FmMapSlotRenderer,
): PreviewMapSlotKey {
  TEST_MAP_SLOT_KEYS.add(key);
  MAP_SLOT_REGISTRY.set(key, renderer);
  invalidateMapSlotDispatcher(key);
  return key as PreviewMapSlotKey;
}

/** Preview-only registration lane for Storybook specimens; never a permanent app identity. */
export function registerPreviewMapSlot<Key extends GovernedMapSlotKey>(
  key: Key,
  renderer: FmMapSlotRendererFor<MapSlotPropsFor<Key>>,
): Key;
export function registerPreviewMapSlot<Key extends string>(
  key: Key,
  renderer: FmMapSlotRenderer,
): Key extends GovernedMapSlotKey ? Key : PreviewMapSlotKey;
export function registerPreviewMapSlot(
  key: string,
  renderer: FmMapSlotRenderer,
): PreviewMapSlotKey | GovernedMapSlotKey {
  PREVIEW_MAP_SLOT_KEYS.add(key);
  if (GOVERNED_MAP_SLOT_KEYS.has(key)) {
    PREVIEW_MAP_SLOT_RENDERERS.set(key, renderer);
    invalidateMapSlotDispatcher(key);
    return key as GovernedMapSlotKey;
  }
  MAP_SLOT_REGISTRY.set(key, renderer);
  invalidateMapSlotDispatcher(key);
  return key as PreviewMapSlotKey;
}

/** Whether a live-map slot renderer is registered for the given key. */
export function hasMapSlot(key: string): boolean {
  return (
    GOVERNED_MAP_SLOT_KEYS.has(key) ||
    TEST_MAP_SLOT_KEYS.has(key) ||
    PREVIEW_MAP_SLOT_KEYS.has(key)
  );
}

/**
 * Look up a registered live-map slot renderer. Returns undefined when the slot
 * is not registered so the DS can show a governed empty state (NO broken frame,
 * NO fabricated map).
 */
export function getMapSlot<Key extends GovernedMapSlotKey>(
  key: Key,
): FmMapSlotRendererFor<MapSlotPropsFor<Key>> | undefined;
export function getMapSlot(key: string): FmMapSlotRenderer | undefined;
export function getMapSlot(key: string): FmMapSlotRenderer | undefined {
  const renderer = MAP_SLOT_REGISTRY.get(key);
  const previewRenderer = PREVIEW_MAP_SLOT_RENDERERS.get(key);
  if (!previewRenderer) return renderer;
  const cachedDispatcher = PREVIEW_MAP_SLOT_DISPATCHERS.get(key);
  if (cachedDispatcher) return cachedDispatcher;

  const dispatcher: FmMapSlotRenderer = (props) => {
    const currentPreviewRenderer = PREVIEW_MAP_SLOT_RENDERERS.get(key);
    if (props.preview && currentPreviewRenderer) {
      return currentPreviewRenderer(props);
    }
    const currentRenderer = MAP_SLOT_REGISTRY.get(key);
    return currentRenderer?.(props) ?? React.createElement(React.Fragment);
  };
  PREVIEW_MAP_SLOT_DISPATCHERS.set(key, dispatcher);
  return dispatcher;
}

/**
 * Test-only: clear all registered slots. Not for production use.
 */
export function __resetMapSlotRegistryForTest(): void {
  MAP_SLOT_REGISTRY.clear();
  TEST_MAP_SLOT_KEYS.clear();
  PREVIEW_MAP_SLOT_KEYS.clear();
  PREVIEW_MAP_SLOT_RENDERERS.clear();
  PREVIEW_MAP_SLOT_DISPATCHERS.clear();
}
